/**
 * `WeighEntry` 的純邏輯：把畫面上的輸入變成 API 的 `weigh` 參數。
 *
 * 刻意與元件分開，因為這一段是**前端與後端之間的契約**：
 * 送錯形狀後端會回 422，而那種錯在瀏覽器上才發現太晚。
 * 分開之後可以用 Node 直接跑（見 scripts/ui-contract.mjs）。
 */

import type { Config, MeasurementPoint } from '../../../shared/types/config.js'
import type { PanelId } from '../../../shared/types/domain.js'
import { round } from '../../../shared/lib/format.js'

export interface WeighValue {
  /** 逐片模式：panelId → 重量字串 */
  perPanel: Record<string, string>
  /** 整批模式 */
  containerId: string
  tareKg: string
  grossKg: string
  scaleId: string
  /** 雙人覆核的第二人（量測點 requiresDualCheck 時必填） */
  verifiedBy: string
}

export function emptyWeighValue(defaults?: Partial<WeighValue>): WeighValue {
  return { perPanel: {}, containerId: '', tareKg: '', grossKg: '', scaleId: '', verifiedBy: '', ...defaults }
}

/** 容器主檔的皮重。`tareVaries` 的容器（來料棧板）回空字串，由現場逐次輸入。 */
export function tareForContainer(config: Config, containerId: string): string {
  const c = config.containers.find((x) => x.containerId === containerId)
  if (!c || c.tareVaries || c.tareKg === null) return ''
  return String(c.tareKg)
}

export function weighNetKg(mp: MeasurementPoint, v: WeighValue, panelIds: PanelId[]): number | null {
  if (mp.granularity === 'panel') {
    const entered = panelIds.filter((p) => v.perPanel[p]?.trim())
    if (entered.length === 0) return null
    return round(entered.reduce((a, p) => a + Number(v.perPanel[p]), 0), 3)
  }
  const g = Number(v.grossKg)
  const t = Number(v.tareKg)
  if (v.grossKg.trim() === '' || Number.isNaN(g)) return null
  return round(g - (Number.isNaN(t) ? 0 : t), 3)
}

/** §8.6 WEIGH_INCOMPLETE 的前端版本：逐片模式下事件數必須等於片數。 */
export function weighComplete(mp: MeasurementPoint, v: WeighValue, panelIds: PanelId[]): boolean {
  if (mp.weightSource === 'booked') return true
  if (!mp.enabled) return true
  if (mp.requiresDualCheck && !v.verifiedBy.trim()) return false
  if (!v.scaleId) return false
  if (mp.granularity === 'panel') {
    return panelIds.length > 0 && panelIds.every((p) => v.perPanel[p]?.trim())
  }
  return v.grossKg.trim() !== ''
}

/**
 * 變成 API 的 `weigh` 參數。
 * `undefined` = 這個量測點不需要量測（帳面繼承或停用），呼叫端不要帶 weigh。
 */
export function toWeighPayload(mp: MeasurementPoint, v: WeighValue, panelIds: PanelId[]) {
  if (mp.weightSource === 'booked' || !mp.enabled) return undefined
  const base = { scaleId: v.scaleId, verifiedBy: v.verifiedBy || null }
  if (mp.granularity === 'panel') {
    const perPanel: Record<string, number> = {}
    for (const p of panelIds) {
      const raw = v.perPanel[p]
      if (raw?.trim()) perPanel[p] = Number(raw)
    }
    return { ...base, perPanel }
  }
  return {
    ...base,
    containerId: v.containerId || null,
    grossKg: v.grossKg.trim() === '' ? null : Number(v.grossKg),
    tareKg: v.tareKg.trim() === '' ? null : Number(v.tareKg),
  }
}
